import React from 'react';
import { apiClient } from '../../api';

interface ConfigureMpxChannelStepProps {
  channelConfig: any;
  configureFormState: any;
  onConfigChange: (config: any, formState?: any) => void;
  onBack: () => void;
}

const ConfigureMpxChannelStep: React.FC<ConfigureMpxChannelStepProps> = ({
  channelConfig,
  configureFormState,
  onConfigChange,
  onBack,
}) => {
  // Initialize from configureFormState if available, otherwise from channelConfig
  const [mediatorId, setMediatorId] = React.useState(configureFormState?.mediator_id || channelConfig?.mediator_id || '');
  const [tenantId, setTenantId] = React.useState(configureFormState?.tenant_id || channelConfig?.tenant_id || '');

  // Note: description is optional
  const [mediators, setMediators] = React.useState<any[]>([]);
  const [tenants, setTenants] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [authenticating, setAuthenticating] = React.useState(false);
  const [authError, setAuthError] = React.useState<string | null>(null);

  // Fetch available mediators and tenants
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [mediatorsResponse, tenantsResponse] = await Promise.all([
          apiClient.get('/mediators/compatible'),
          apiClient.get('/mpx-tenants').then(r => r.data.filter((t: any) => t.enabled === true))
        ]);
        
        const mediatorsList = Array.isArray(mediatorsResponse.data) ? mediatorsResponse.data : [];
        const tenantsList = Array.isArray(tenantsResponse) ? tenantsResponse : [];
        
        setMediators(mediatorsList);
        setTenants(tenantsList);
        
        // Auto-select the first mediator if there's only one and no mediator is already selected
        if (mediatorsList.length === 1 && !mediatorId) {
          setMediatorId(mediatorsList[0].id);
        }
        
        // Auto-select the first tenant if there's only one and no tenant is already selected
        if (tenantsList.length === 1 && !tenantId) {
          setTenantId(tenantsList[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch mediators or tenants:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNext = async () => {
    if (mediatorId && tenantId) {
      setAuthenticating(true);
      setAuthError(null);
      
      try {
        // Authenticate to MPX tenant (token stored server-side only)
        await apiClient.post('/mpx-tenants/authenticate', {
          tenant_id: tenantId,
        });
        
        const config = {
          ...channelConfig,
          mediator_id: mediatorId,
          tenant_id: tenantId,
          protocol: 'mpx',
        };
        const formState = {
          mediator_id: mediatorId,
          tenant_id: tenantId,
          selectedHostPort: '',
          selectedPrefix: '',
          customPath: '',
          target_endpoint: '',
        };
        onConfigChange(config, formState);
      } catch (error: any) {
        console.error('Failed to authenticate to MPX tenant:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to authenticate to MPX tenant';
        setAuthError(errorMessage);
      } finally {
        setAuthenticating(false);
      }
    }
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <div className="configure-mpx-step">
          <h5 className="mb-4">
            <span className="badge badge-warning mr-2">MPX</span>
            Configure MPX Channel
          </h5>

          <div className="form-group">
            <label htmlFor="mpx-mediator">
              Mediator <span className="text-danger">*</span>
            </label>
            {loading ? (
              <div className="text-muted">
                <i className="fas fa-spinner fa-spin"></i> Loading mediators...
              </div>
            ) : mediators.length === 0 ? (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle"></i> No compatible mediators configured.
                Please add a compatible mediator first.
              </div>
            ) : (
              <>
                <select
                  className="form-control dropdown-styling"
                  id="mpx-mediator"
                  value={mediatorId}
                  onChange={(e) => setMediatorId(e.target.value)}
                  required
                >
                  <option value="">Select a mediator...</option>
                  {mediators.map((mediator) => (
                    <option key={mediator.id} value={mediator.id}>
                      {mediator.name || 'Unnamed Mediator'} - {mediator.did}
                    </option>
                  ))}
                </select>
                
                {mediatorId && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <h6 className="text-primary mb-2">
                      <i className="fas fa-info-circle mr-2"></i>
                      Selected Mediator Details
                    </h6>
                    {(() => {
                      const selected = mediators.find(m => m.id === mediatorId);
                      if (!selected) return null;
                      return (
                        <>
                          <div className="mb-2">
                            <strong>Name:</strong> {selected.name || 'Unnamed Mediator'}
                          </div>
                          {selected.description && (
                            <div className="mb-2">
                              <strong>Description:</strong> {selected.description}
                            </div>
                          )}
                          <div className="mb-0">
                            <strong>DID:</strong> 
                            <code className="ml-2 text-break">{selected.did}</code>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="mpx-tenant">
              MPX Tenant <span className="text-danger">*</span>
            </label>
            {loading ? (
              <div className="text-muted">
                <i className="fas fa-spinner fa-spin"></i> Loading MPX tenants...
              </div>
            ) : tenants.length === 0 ? (
              <div className="alert alert-warning">
                <i className="fas fa-exclamation-triangle"></i> No active MPX tenants configured.
                Please add an MPX tenant first.
              </div>
            ) : (
              <>
                <select
                  className="form-control dropdown-styling"
                  id="mpx-tenant"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  required
                >
                  <option value="">Select an MPX tenant...</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} - {tenant.did}
                    </option>
                  ))}
                </select>
                
                {tenantId && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <h6 className="text-primary mb-2">
                      <i className="fas fa-info-circle mr-2"></i>
                      Selected MPX Tenant Details
                    </h6>
                    {(() => {
                      const selected = tenants.find(t => t.id === tenantId);
                      if (!selected) return null;
                      return (
                        <>
                          <div className="mb-2">
                            <strong>Name:</strong> {selected.name}
                          </div>
                          {selected.description && (
                            <div className="mb-2">
                              <strong>Description:</strong> {selected.description}
                            </div>
                          )}
                          <div className="mb-0">
                            <strong>API Endpoint:</strong> 
                            <code className="ml-2 text-break">{selected.api_endpoint}</code>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
          </div>

          {authError && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              <strong>Authentication Error:</strong> {authError}
            </div>
          )}

          <div className="d-flex justify-content-between mt-4">
            <button className="btn btn-secondary" onClick={onBack}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <button
              className="btn btn-primary"
              onClick={handleNext}
              disabled={!mediatorId || !tenantId || authenticating}
            >
              {authenticating ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Authenticating...
                </>
              ) : (
                <>
                  Next <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureMpxChannelStep;
